import { CircularProgress, List, TextField, Box } from '@mui/material';
import { useCallback, memo, useMemo, useState, useEffect } from 'react';
import { useStyles } from './styles/Tree.styles';

import type { Dispatch, SetStateAction } from 'react';
import { useNameMapQuery, useOrgMapQuery } from '@features/redux/apis/org';
import { Selection, type Node, type NodeGroup, GroupType } from './types';
import type { TreeState } from './useTreeState';
import { TreeNodes } from './TreeNodes';
import { filterNodes, getTreeState } from './utils';

const ListMemo = memo(List);

type TreeProps = {
    selection: TreeState;
    setSelection: Dispatch<SetStateAction<TreeState>>;
    onSelect?: () => void;
    root: HTMLDivElement | null;
};

export const Tree = ({ root, selection, setSelection, onSelect }: TreeProps) => {
    const classes = useStyles();

    const [search, setSearch] = useState('');

    const {
        data: { companyLocationMap = {}, companyResellerMap = {}, resellerLocationMap = {} } = {},
        isFetching: orgMapLoading,
    } = useOrgMapQuery({});
    const { data: { companies = {}, resellers = {}, locations = {} } = {}, isFetching: nameMapLoading } =
        useNameMapQuery({});

    const loading = orgMapLoading || nameMapLoading;

    // todo: Abstract this tree formatting logic
    // ! Draft
    const nodes = useMemo<Node[]>(() => {
        return Object.entries(companies)
            .map(([companyId, companyName]) => ({
                type: Selection.Company,
                id: +companyId,
                name: companyName,
                children: [
                    {
                        type: GroupType.Resellers,
                        nodesType: Selection.Reseller,
                        name: 'Resellers',
                        nodes: (
                            companyResellerMap[companyId]?.map((resellerId) => ({
                                type: Selection.Reseller,
                                id: resellerId,
                                name: resellers[resellerId],
                                children: [
                                    {
                                        type: GroupType.ResellerLocations,
                                        nodesType: Selection.Location,
                                        name: 'Locations',
                                        nodes: (
                                            resellerLocationMap[resellerId]?.map((locationId) => ({
                                                type: Selection.Location,
                                                id: locationId,
                                                name: locations[locationId],
                                                children: [],
                                            })) ?? []
                                        ).sort((a, b) => a.name.localeCompare(b.name)),
                                    },
                                ],
                            })) ?? []
                        ).sort((a, b) => a.name.localeCompare(b.name)),
                    },
                    {
                        type: GroupType.Locations,
                        nodesType: Selection.Location,
                        name: 'Locations',
                        nodes: (
                            companyLocationMap[companyId]?.map((locationId) => ({
                                type: Selection.Location,
                                id: locationId,
                                name: locations[locationId],
                                children: [],
                            })) ?? []
                        ).sort((a, b) => a.name.localeCompare(b.name)),
                    },
                ],
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [companies, companyResellerMap, resellers, resellerLocationMap, companyLocationMap, locations]);

    const filtered = useMemo(() => filterNodes(nodes, search), [nodes, search]);

    useEffect(() => {
        if (!search || !filtered.length) return;

        setSelection(getTreeState(filtered[0]));
    }, [filtered, search]);

    const DESELECT_RESET = {
        [Selection.Company]: {
            [Selection.Company]: null,
            [Selection.Reseller]: null,
            [Selection.Location]: null,
            type: null,
            groups: [],
        },
        [Selection.Reseller]: {
            [Selection.Reseller]: null,
            [Selection.Location]: null,
            type: Selection.Company,
        },
        [Selection.Location]: {
            [Selection.Location]: null,
            type: Selection.Company,
        },
    };

    const handleNodeSelect = useCallback(
        ({ id, type }: Node) => {
            setSelection((prev) => ({
                ...prev,
                // ? Deselection occurs
                ...(prev[type] === id
                    ? DESELECT_RESET[type]
                    : {
                          type,
                          [type]: id,
                      }),
            }));

            onSelect?.();
        },
        [setSelection, onSelect]
    );

    const handleGroupSelect = useCallback(
        (group: NodeGroup) => {
            setSelection((prev) => {
                const isDeselect = prev.groups.includes(group.type);

                return {
                    ...prev,
                    // This de-selects the group
                    groups: isDeselect
                        ? prev.groups.filter((type) => type !== group.type)
                        : [...prev.groups, group.type],
                };
            });
        },
        [setSelection]
    );

    return (
        <>
            <Box className={classes.searchArea}>
                <TextField
                    autoFocus
                    placeholder='Search organizations...'
                    onChange={(e) => setSearch(e.target.value)}
                    value={search}
                    size='small'
                    autoComplete='off'
                    autoCorrect='off'
                    helperText={search ? `Showing results for "${search}"` : ' '}
                    inputProps={{ type: 'search' }}
                />
            </Box>

            {loading && (
                <Box display='flex' justifyContent='center' alignItems='center' width='100%'>
                    <CircularProgress size='3rem' />
                </Box>
            )}

            {!loading && (
                <ListMemo dense={true} disablePadding>
                    <TreeNodes
                        nodes={filtered}
                        root={root}
                        selection={selection}
                        onNodeSelect={handleNodeSelect}
                        onGroupSelect={handleGroupSelect}
                    />
                </ListMemo>
            )}
        </>
    );
};
